// How --device-bottom-inset gets its value. No React and no module-scope DOM, so node --test can
// drive the priority order directly.

import { readEnvNumber } from '../api/env.ts';
import { readJson, removeKey, writeJson } from '../api/storage.ts';

/** Sane ceiling for a dock strip - anything past this is a typo, not a device. */
export const MAX_INSET = 200;

/** Per-device override the viewport probe writes, so a dialled-in dock survives a reload. */
export const INSET_STORAGE_KEY = 'izqlt.device.bottomInset';

export type InsetSource = 'query' | 'stored' | 'native' | 'build' | 'none';

/** What the Android wrapper writes onto window on every inset dispatch. */
export interface NativeInsets {
  top?: number;
  bottom?: number;
  tappableBottom?: number;
  navVisible?: boolean;
}

/** The wrapper re-publishes on every dispatch; the first one can land on about:blank, so the event is the source of truth. */
export const NATIVE_INSET_EVENT = 'nativeinsets';

export interface ResolvedInset {
  inset: number;
  source: InsetSource;
}

export function clampInset(value: number): number {
  if (isNaN(value)) return 0;
  return Math.min(Math.max(Math.round(value), 0), MAX_INSET);
}

/** `?bottomInset=<px>` beats the build value so the presenter can dial the dock in on-site. */
export function readQueryInset(): number | undefined {
  const raw = new URLSearchParams(window.location.search).get('bottomInset');
  if (raw === null || raw.trim() === '') return undefined;
  const parsed = Number(raw);
  return isNaN(parsed) ? undefined : clampInset(parsed);
}

export function readStoredInset(): number | undefined {
  const raw = readJson<number>(INSET_STORAGE_KEY);
  return typeof raw === 'number' && !isNaN(raw) ? clampInset(raw) : undefined;
}

export function writeStoredInset(value: number): void {
  writeJson(INSET_STORAGE_KEY, clampInset(value));
}

export function clearStoredInset(): void {
  removeKey(INSET_STORAGE_KEY);
}

export function readNativeInsets(): NativeInsets | undefined {
  const scope = window as unknown as { __nativeInsets?: NativeInsets };
  const raw = scope.__nativeInsets;
  return raw && typeof raw === 'object' ? raw : undefined;
}

/** Presence alone decides: if the wrapper is reporting, a 0 means the bar is genuinely gone, not that nothing was measured. */
export function nativeLayerPresent(): boolean {
  return readNativeInsets() !== undefined;
}

/** tappableBottom is 0 under gesture nav, where the systemBars() bottom would over-reserve ~24dp. */
export function readNativeInset(): number {
  const insets = readNativeInsets();
  if (!insets) return 0;
  const raw = typeof insets.tappableBottom === 'number' ? insets.tappableBottom : insets.bottom;
  return typeof raw === 'number' ? clampInset(raw) : 0;
}

/** Tauri v2 injects __TAURI_INTERNALS__ into every document it loads; __TAURI__ needs withGlobalTauri, which the APK never sets. */
function inTauriShell(): boolean {
  const scope = window as unknown as { __TAURI_INTERNALS__?: unknown };
  if (scope.__TAURI_INTERNALS__) return true;
  // the embedded APK serves from tauri.localhost, the remote one from msb.smoke-ant.com - only the first is identifiable by origin
  return /(^|\.)tauri\.localhost$/i.test(window.location.hostname) || window.location.protocol === 'tauri:';
}

/** Opened in the panel's own browser there is no Tauri marker left, so fall back to Android plus a touch screen. */
function onAndroidTouch(): boolean {
  const nav = window.navigator;
  if (!nav || !/android/i.test(nav.userAgent || '')) return false;
  return (nav.maxTouchPoints || 0) > 0 || 'ontouchstart' in window;
}

/** Only a device that could carry the launcher dock may claim the build inset - everything else, desktop Chrome included, gets 0. */
export function isPanelRuntime(): boolean {
  if (typeof window === 'undefined') return false;
  return inTauriShell() || onAndroidTouch();
}

/** Priority: ?bottomInset, the stored override, the native inset, then the build value but only on a panel with no native layer, else 0. */
export function resolveInset(): ResolvedInset {
  const query = readQueryInset();
  if (query !== undefined) return { inset: query, source: 'query' };
  const stored = readStoredInset();
  if (stored !== undefined) return { inset: stored, source: 'stored' };
  if (nativeLayerPresent()) return { inset: readNativeInset(), source: 'native' };
  // legacy path: the old APK and the panel's own browser, neither of which reports insets
  if (isPanelRuntime()) return { inset: clampInset(readEnvNumber('VITE_DEVICE_BOTTOM_INSET', 0)), source: 'build' };
  return { inset: 0, source: 'none' };
}

/** `?viewportProbe=1` arms the diagnostic overlay; read here so the hook can gate the import without pulling the module in. */
export function probeRequested(): boolean {
  const raw = new URLSearchParams(window.location.search).get('viewportProbe');
  return raw !== null && raw !== '0' && raw.toLowerCase() !== 'false';
}
